'use client'

import { useQueryClient } from '@tanstack/react-query'
import { useAtom, useAtomValue } from 'jotai'
import { selectAtom } from 'jotai/utils'
import { useCallback, useContext, useEffect, useMemo, useRef } from 'react'

import { useAuth } from '@/features/auth/hooks'
import { useAuthGuard } from '@/features/auth/hooks/use-auth-guard'
import { getExpiryTimestamp, parseOptionSymbol } from '@/features/chart/lib/utils/option-util'
import { LinkingPageContext } from '@/features/dashboard/lib/linking-page-context'
import { isSharePreviewPageId } from '@/features/dashboard/lib/share-layout'
import { uiStateKeys, useUpdateWidget } from '@/service/ui-state.hook'
import { activePageIdAtom } from '@/state/dashboard-layouts'
import type { LinkingColor } from '@/state/linking'
import { linkingGroupStateAtomFamily, type OptionChainOverride } from '@/state/linking-group'
import { guidePreferencesReadyAtom } from '@/state/onboarding'
import {
  type OrderTypeGuideKey,
  type OrderTypeGuideSeen,
  orderTypeGuideSeenAtom,
} from '@/state/order-type-guide'
import type { WatchList } from '@/state/storage'
import type { GetPageLayoutResponse } from '@/types/generated/public_api'

// ─── Constants ──────────────────────────────────────────────────────────────

export const DATA_MANAGER_PREFIX = 'data-manager-'

export function getDataManagerWidgetId(pageId: string): string {
  return `${DATA_MANAGER_PREFIX}${pageId}`
}

export function isDataManagerWidget(widgetId: string): boolean {
  return widgetId.startsWith(DATA_MANAGER_PREFIX)
}

// ─── Types ───────────────────────────────────────────────────────────────────

/**
 * WatchList item with runtime price fields stripped.
 * Only stable metadata that should be persisted across devices.
 */
export type StrippedWatchListItem = {
  id: string
  symbol: string
  type: 'spot' | 'future' | 'perps' | 'options'
  exchange: string
  accountId?: string
  pricePrecision?: string
  timeframe?: number
  expiry?: string
  expirationTsMs?: string
}

/**
 * Per-color state stored in data-manager config.
 * Extensible for future fields (positionFilter, chartOption, etc.)
 */
export interface DataManagerColorState {
  tabs?: {
    list: StrippedWatchListItem[]
    focusedTabId?: string | null
    selectedIndex: number
  }
  optionOverride?: OptionChainOverride | null
}

type ExpirableTab = Pick<
  StrippedWatchListItem,
  'id' | 'symbol' | 'type' | 'exchange' | 'expiry' | 'expirationTsMs'
>

const ONE_DAY_MS = 24 * 60 * 60 * 1000
const DERIBIT_EXPIRY_HOUR_UTC = 8

/**
 * The date token of a dated-future symbol (`BTC-27DEC24`, `BTC_USDC-14OCT26`), which
 * parseOptionSymbol doesn't read: it only knows options (`…-STRIKE-C|P`).
 */
function parseDatedSymbolExpiry(symbol: string): string | undefined {
  return symbol.toUpperCase().match(/-(\d{1,2}[A-Z]{3}\d{2})(?=-|$)/)?.[1]
}

function getFallbackExpirationMs(tab: ExpirableTab): number | null {
  const parsedOption = parseOptionSymbol(tab.symbol)
  const expiry = tab.expiry ?? parsedOption?.expiry ?? parseDatedSymbolExpiry(tab.symbol)
  if (!expiry) return null

  const expiryDateMs = getExpiryTimestamp(expiry)
  if (!Number.isFinite(expiryDateMs) || expiryDateMs <= 0) return null

  // Legacy persisted tabs may not contain expirationTsMs. Deribit dated
  // instruments settle at 08:00 UTC; for unknown venues keep the conservative
  // end-of-UTC-day fallback instead of removing a still-trading contract early.
  const fallbackOffsetMs =
    tab.exchange.toUpperCase() === 'DERIBIT' ? DERIBIT_EXPIRY_HOUR_UTC * 60 * 60 * 1000 : ONE_DAY_MS
  return expiryDateMs + fallbackOffsetMs
}

/**
 * When a tab's instrument expires (epoch ms), or null when it never does (spot, perps) or
 * can't be told. The exact `expirationTsMs` wins; legacy tabs fall back to the symbol's date.
 */
export function getTabExpirationMs(tab: ExpirableTab): number | null {
  const exactExpirationMs = Number(tab.expirationTsMs)
  if (Number.isFinite(exactExpirationMs) && exactExpirationMs > 0) return exactExpirationMs

  // A tab opened without instrument metadata (no expirationTsMs, or typed as a perp) still
  // expires when its symbol carries a date.
  const isDatedType = tab.type === 'options' || tab.type === 'future'
  const isDatedSymbol =
    parseOptionSymbol(tab.symbol) !== null || parseDatedSymbolExpiry(tab.symbol) !== undefined
  if (!isDatedType && !isDatedSymbol) return null

  return getFallbackExpirationMs(tab)
}

function isExpiredTab(tab: ExpirableTab, nowMs: number): boolean {
  const expirationMs = getTabExpirationMs(tab)
  return expirationMs !== null && expirationMs <= nowMs
}

/**
 * Removes expired dated-instrument tabs while preserving the focused tab whenever
 * possible. If the focused tab expired, focus moves to its nearest surviving tab
 * on the left, then falls back to the first tab.
 */
export function pruneExpiredWatchListTabs<
  T extends ExpirableTab,
  TWatchList extends { list: T[]; focusedTabId?: string | null; selectedIndex: number },
>(watchList: TWatchList, nowMs: number): TWatchList {
  if (
    !Number.isFinite(nowMs) ||
    nowMs <= 0 ||
    !watchList.list.some((tab) => isExpiredTab(tab, nowMs))
  ) {
    return watchList
  }

  const focusedIndex = watchList.focusedTabId
    ? watchList.list.findIndex((tab) => tab.id === watchList.focusedTabId)
    : watchList.selectedIndex
  const selectedIndex = focusedIndex >= 0 ? focusedIndex : watchList.selectedIndex
  const selectedTab = watchList.list[selectedIndex]
  const nextList = watchList.list.filter((tab) => !isExpiredTab(tab, nowMs))

  if (nextList.length === 0) {
    return { ...watchList, list: [], focusedTabId: null, selectedIndex: -1 } as TWatchList
  }

  if (selectedTab && !isExpiredTab(selectedTab, nowMs)) {
    const nextSelectedIndex = nextList.findIndex((tab) => tab.id === selectedTab.id)
    return {
      ...watchList,
      list: nextList,
      focusedTabId: selectedTab.id,
      selectedIndex: nextSelectedIndex,
    } as TWatchList
  }

  const previousTab = watchList.list
    .slice(0, Math.max(selectedIndex, 0))
    .reverse()
    .find((tab) => !isExpiredTab(tab, nowMs))
  const nextSelectedIndex = previousTab ? nextList.findIndex((tab) => tab.id === previousTab.id) : 0

  return {
    ...watchList,
    list: nextList,
    focusedTabId: nextList[nextSelectedIndex].id,
    selectedIndex: nextSelectedIndex,
  } as TWatchList
}

// ─── Utilities ───────────────────────────────────────────────────────────────

/**
 * Merge a partial update into existing DataManagerColorState without clobbering
 * unrelated fields. Use this whenever writing only one field (tabs or optionOverride)
 * to prevent tabs sync from wiping the override and vice-versa.
 */
export function mergeColorState(
  existing: DataManagerColorState,
  patch: Partial<DataManagerColorState>
): DataManagerColorState {
  return { ...existing, ...patch }
}

/**
 * Strip runtime price fields from WatchList before server sync.
 * Keeps only stable metadata that is meaningful across devices.
 */
export function stripWatchList(watchList: WatchList): DataManagerColorState['tabs'] {
  return {
    focusedTabId: watchList.focusedTabId ?? watchList.list[watchList.selectedIndex]?.id ?? null,
    selectedIndex: watchList.selectedIndex,
    list: watchList.list.map(
      ({
        id,
        symbol,
        type,
        exchange,
        accountId,
        pricePrecision,
        timeframe,
        expiry,
        expirationTsMs,
      }) => ({
        id,
        symbol,
        type,
        exchange,
        ...(accountId !== undefined ? { accountId } : {}),
        ...(pricePrecision !== undefined ? { pricePrecision } : {}),
        ...(timeframe !== undefined ? { timeframe } : {}),
        ...(expiry !== undefined ? { expiry } : {}),
        ...(expirationTsMs !== undefined ? { expirationTsMs } : {}),
      })
    ),
  }
}

// ─── Hook ────────────────────────────────────────────────────────────────────

/**
 * Sync tab metadata for a linking color to the data-manager virtual widget.
 * Debounces at 500ms so rapid tab changes are batched into one API call.
 *
 * Usage:
 *   const { syncTabs } = useDataManagerSync()
 *   syncTabs('mint', watchList)
 */
export function useDataManagerSync() {
  const activePageId = useAtomValue(activePageIdAtom)
  const contextPageId = useContext(LinkingPageContext)
  const { isLoggedIn } = useAuthGuard()
  const { user } = useAuth()
  const queryClient = useQueryClient()
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  // Every color changed within the debounce window, flushed together: one shared timer per
  // color would drop the earlier color (a tab moved between two groups writes both), and
  // separate POSTs would each overwrite the whole config from the same stale cache.
  const pendingRef = useRef(new Map<LinkingColor, WatchList>())

  const { mutate: doUpdateWidget } = useUpdateWidget({
    onError: (err) => console.error('[useDataManagerSync]', err),
  })

  // Clear pending debounce on unmount to prevent stale API calls
  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current)
    }
  }, [])

  /**
   * Sync tabs for a given linking color to the server.
   * Skips unlinked cards (those are device-local only).
   * Guards against race condition where data-manager widget may not exist yet.
   */
  const syncTabs = useCallback(
    (color: LinkingColor, watchList: WatchList) => {
      if (!isLoggedIn || !activePageId || color === 'unlinked') return
      // A shared-layout preview is read-only, and activePageId still points at
      // the viewer's own page — syncing here would write the share's tabs into
      // it. Every other caller keys off the page it is actually rendering.
      if (isSharePreviewPageId(contextPageId)) return

      pendingRef.current.set(color, watchList)
      if (debounceRef.current) clearTimeout(debounceRef.current)

      debounceRef.current = setTimeout(() => {
        const pending = pendingRef.current
        pendingRef.current = new Map()
        const dmId = getDataManagerWidgetId(activePageId)

        // Read current full config from cache to preserve other colors
        const layoutData = queryClient.getQueryData<GetPageLayoutResponse>(
          uiStateKeys.pageLayout(user?.userId, activePageId)
        )

        // Guard: skip if data-manager widget hasn't been created on server yet
        // (DataManagerSync handles lazy init; sync will resume after next tab change)
        if (!layoutData?.widgets.some((w) => w.widgetId === dmId)) return

        const dmWidget = layoutData.widgets.find((w) => w.widgetId === dmId)
        const existingConfig = dmWidget?.config ?? {}
        const config = { ...existingConfig }
        let changed = false

        for (const [pendingColor, pendingWatchList] of pending) {
          const prevColorState: DataManagerColorState = existingConfig[pendingColor]
            ? (JSON.parse(existingConfig[pendingColor]) as DataManagerColorState)
            : {}

          const nextTabs = stripWatchList(pendingWatchList)

          // Skip no-op writes: syncTabs is called once per mounted widget instance
          // that shares this color (see useWatchlistTab), so several widgets can
          // each fire this on mount with tabs that are already what's on the
          // server. Without this guard every one of those mounts re-POSTs the
          // same config.
          if (JSON.stringify(prevColorState.tabs ?? null) === JSON.stringify(nextTabs ?? null)) {
            continue
          }
          config[pendingColor] = JSON.stringify(mergeColorState(prevColorState, { tabs: nextTabs }))
          changed = true
        }
        if (!changed) return

        doUpdateWidget({
          pageId: activePageId,
          widgetId: dmId,
          config,
        })
      }, 500)
    },
    [isLoggedIn, activePageId, contextPageId, user?.userId, queryClient, doUpdateWidget]
  )

  return { syncTabs }
}

// ─── Option override sync ────────────────────────────────────────────────────

/**
 * Sync the optionChainOverride for one linking color to the data-manager widget.
 *
 * Mounted exactly once per color from `DataManagerSync` (once per page, regardless of
 * how many price-chart widgets share that color) — NOT called per-widget/per-cardId.
 * Reading and setting `optionChainOverride` remains scoped to the owning chart widget
 * (see TabGroupedChart's ownership-gated set/clear effects); this hook only handles
 * *persisting* whatever value is currently in group state for `linkingColor`, exactly
 * once, regardless of how many chart instances share that color. Uses mergeColorState
 * so existing tabs are never clobbered.
 */
export function useOptionOverrideSync(linkingColor: LinkingColor) {
  const pageId = useContext(LinkingPageContext)
  const { isLoggedIn } = useAuthGuard()
  const { user } = useAuth()
  const queryClient = useQueryClient()
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  // One of these is mounted per linking color (see data-manager-sync.tsx), so
  // subscribing to the whole per-page map meant every write to any color's state
  // re-ran all of them. Narrowed to just this color's optionChainOverride.
  const optionChainOverride = useAtomValue(
    useMemo(
      () =>
        selectAtom(
          linkingGroupStateAtomFamily(pageId),
          (map) => map[linkingColor]?.optionChainOverride ?? null
        ),
      [pageId, linkingColor]
    )
  )

  const { mutate: doUpdateWidget } = useUpdateWidget({
    onError: (err) => console.error('[useOptionOverrideSync]', err),
  })

  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current)
    }
  }, [])

  useEffect(() => {
    if (!isLoggedIn || !pageId || pageId === 'default') return

    if (debounceRef.current) clearTimeout(debounceRef.current)

    debounceRef.current = setTimeout(() => {
      const dmId = getDataManagerWidgetId(pageId)

      const layoutData = queryClient.getQueryData<GetPageLayoutResponse>(
        uiStateKeys.pageLayout(user?.userId, pageId)
      )

      if (!layoutData?.widgets.some((w) => w.widgetId === dmId)) return

      const dmWidget = layoutData.widgets.find((w) => w.widgetId === dmId)
      const existingConfig = dmWidget?.config ?? {}

      const prevColorState: DataManagerColorState = existingConfig[linkingColor]
        ? (JSON.parse(existingConfig[linkingColor]) as DataManagerColorState)
        : {}

      // Skip no-op writes: this effect fires on every mount (e.g. page/tab switch
      // remounts DashboardLayout), not just on genuine override changes. Without
      // this guard, every remount re-POSTs the same config for all 7 colors.
      if (
        JSON.stringify(prevColorState.optionOverride ?? null) ===
        JSON.stringify(optionChainOverride ?? null)
      ) {
        return
      }

      doUpdateWidget({
        pageId,
        widgetId: dmId,
        config: {
          ...existingConfig,
          [linkingColor]: JSON.stringify(
            mergeColorState(prevColorState, { optionOverride: optionChainOverride })
          ),
        },
      })
    }, 500)
    // Re-run whenever override changes (optionChainOverride reference changes)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [optionChainOverride, isLoggedIn, pageId, linkingColor, user?.userId])
}

// ─── Order type guide ────────────────────────────────────────────────────────

export function parseOrderTypeGuideConfig(
  json: string | undefined
): Record<string, OrderTypeGuideSeen> {
  if (!json) return {}
  try {
    const parsed = JSON.parse(json) as unknown
    return parsed && typeof parsed === 'object'
      ? (parsed as Record<string, OrderTypeGuideSeen>)
      : {}
  } catch {
    return {}
  }
}

/**
 * Which first-use order type guides the signed-in user has dismissed, and a writer that
 * records one. The shared atom is the source of truth; `useGuidesSync` saves it to the
 * user's own preference record, so a dismissal holds on every page and device. Until that
 * record has loaded, a signed-in user's guides count as seen, so one they already dismissed
 * elsewhere doesn't flash up and vanish.
 */
export function useOrderTypeGuide() {
  const { isLoggedIn } = useAuthGuard()
  const { user } = useAuth()
  const ready = useAtomValue(guidePreferencesReadyAtom)
  const [seenByUser, setSeenByUser] = useAtom(orderTypeGuideSeenAtom)

  // Signed out there is no userId to persist under; the guide still dismisses for the session.
  const ownerKey = user?.userId ?? 'guest'
  const seen = seenByUser[ownerKey]
  const loading = isLoggedIn && !ready

  const markSeen = useCallback(
    (key: OrderTypeGuideKey) => {
      setSeenByUser((prev) =>
        prev[ownerKey]?.[key] ? prev : { ...prev, [ownerKey]: { ...prev[ownerKey], [key]: true } }
      )
    },
    [ownerKey, setSeenByUser]
  )

  const isSeen = useCallback(
    (key: OrderTypeGuideKey) => loading || Boolean(seen?.[key]),
    [loading, seen]
  )

  return { isSeen, markSeen }
}
